import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle, Home } from 'lucide-react';
import { tenantUrl } from '../../utils/tenantResolver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumForgotPasswordPage({ tenantConfig }) {
  const [searchParams] = useSearchParams();
  const tenantSlug = tenantConfig?.slug || searchParams.get('tenant') || '';
  const tenantName = tenantConfig?.name || '';
  const logoUrl = tenantConfig?.logo_url || '';

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (res.data.reset_token) setResetToken(res.data.reset_token);
      toast.success('Reset instructions sent');
    } catch {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const loginUrl = tenantUrl('/login', tenantSlug);
  const resetUrl = tenantUrl(`/reset-password?token=${resetToken}`, tenantSlug);

  return (
    <div className="prem-auth-page" data-testid="premium-forgot-password-page">
      <div className="prem-auth-window">
        {/* Browser Chrome */}
        <div className="prem-auth-chrome">
          <div className="prem-auth-dots">
            <span className="prem-dot prem-dot-red" />
            <span className="prem-dot prem-dot-yellow" />
            <span className="prem-dot prem-dot-green" />
          </div>
        </div>

        {/* Blue gradient header */}
        <div className="prem-auth-header-bar">
          <div className="prem-auth-brand-wrap">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="prem-auth-brand-logo" />
            ) : (
              <div className="prem-auth-brand-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M5 3l14 9-14 9V3z"/></svg>
              </div>
            )}
            <span className="prem-auth-brand-text">DepinaCRM</span>
          </div>
        </div>

        {/* White body */}
        <div className="prem-auth-body">
          {!sent ? (
            <>
              <h1 className="prem-auth-welcome">Reset Password</h1>
              <p className="prem-auth-subtitle">Enter your email and we'll send you a reset link.</p>

              {tenantSlug && (
                <div className="prem-auth-tenant-box" data-testid="premium-forgot-tenant-badge">
                  <div className="prem-auth-tenant-row prem-auth-tenant-domain">
                    <Home size={18} />
                    <span>{tenantSlug}.depinacrm.com</span>
                  </div>
                  <div className="prem-auth-tenant-divider" />
                  <div className="prem-auth-tenant-row prem-auth-tenant-name">
                    <Home size={16} />
                    <span>{tenantName || tenantSlug}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="prem-auth-form" data-testid="premium-forgot-form">
                <div className="prem-auth-field">
                  <label>Email Address</label>
                  <div className="prem-auth-input-wrap">
                    <Mail size={18} className="prem-auth-input-icon" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="user@example.com"
                      data-testid="premium-forgot-email"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="prem-auth-submit"
                  disabled={loading}
                  data-testid="premium-forgot-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="prem-auth-success-block" data-testid="premium-forgot-success">
              <CheckCircle size={52} className="prem-auth-success-check" />
              <h1 className="prem-auth-welcome">Check Your Email</h1>
              <p className="prem-auth-subtitle">
                If an account exists for {email}, a reset link has been sent.
              </p>
              {resetToken && (
                <Link
                  to={resetUrl}
                  className="prem-auth-submit"
                  style={{ textDecoration: 'none', marginTop: 12 }}
                  data-testid="premium-forgot-reset-link"
                >
                  Reset Password Now
                </Link>
              )}
            </div>
          )}

          <Link to={loginUrl} className="prem-auth-back-link" data-testid="premium-forgot-back">
            <ArrowLeft size={16} /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
